import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell, AuthField } from "@/components/AuthShell";
import { BArecordMark } from "@/components/BArecordMark";
import { useSafeMeAuth, formatApiError } from "@/context/SafeMeAuthContext";

function SafeMeBrand() {
  return (
    <div className="flex items-center gap-2.5" data-testid="safeme-brand">
      <BArecordMark size={20} className="text-paper" />
      <span className="font-display text-lg tracking-tight text-paper">BArecord</span>
      <span className="text-[9px] uppercase tracking-widest bg-white/10 text-paper rounded-full px-1.5 py-0.5">by Aliwer</span>
    </div>
  );
}

export default function SafeMeRegister() {
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, user } = useSafeMeAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/barecord", { replace: true });
  }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/barecord");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      brand={<SafeMeBrand />}
      tagline="BArecord — Business Archive Record: capture, secure & understand every conversation."
      panelHeadline="Business. Captured. Scored. Governed."
      title="Create your BArecord account"
      subtitle="A dedicated, secure account for BArecord — separate from your Aliwer client or partner login."
      image="https://static.prod-images.emergentagent.com/jobs/a7857c63-2010-4de3-a354-ef80fc69c639/images/29c5a35a6953ad6be50a2bfca7e8fe84874ff7ea36a30de0c488e32f298c2cca.png"
      foot={
        <>
          Already have a BArecord account?{" "}
          <Link to="/barecord/login" className="text-paper underline underline-offset-4" data-testid="safeme-to-login">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="safeme-register-form">
        <AuthField label="Full name" value={form.name} onChange={set("name")} required testid="safeme-register-name" />
        <AuthField label="Work email" type="email" value={form.email} onChange={set("email")} required testid="safeme-register-email" />
        <AuthField label="Company (optional)" value={form.company} onChange={set("company")} testid="safeme-register-company" />
        <AuthField label="Password" type="password" value={form.password} onChange={set("password")} required minLength={6} testid="safeme-register-password" />
        {error && <p className="text-sm text-red-400" data-testid="safeme-register-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="safeme-register-submit"
          className="w-full rounded-full bg-paper text-black text-sm font-medium py-3.5 hover:bg-white transition-colors disabled:opacity-60"
        >
          {loading ? "Creating account…" : "Create BArecord account"}
        </button>
      </form>
    </AuthShell>
  );
}
